import React from "react";

const MAX_BULK_ITEMS = 20;

export interface BulkActionsToolbarProps {
  selectedCount: number;
  totalCount: number;
  onApprove: () => void;
  onReject: () => void;
  isLoading: boolean;
}

/**
 * Toolbar for bulk approve/reject operations
 * Appears above the recommendations table when items are selected
 */
export function BulkActionsToolbar({
  selectedCount,
  totalCount,
  onApprove,
  onReject,
  isLoading,
}: BulkActionsToolbarProps) {
  const isOverLimit = selectedCount > MAX_BULK_ITEMS;
  const isDisabled = selectedCount === 0 || isOverLimit || isLoading;

  if (selectedCount === 0) {
    return null;
  }

  return (
    <div
      className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4 flex items-center gap-4"
      role="toolbar"
      aria-label="Akcje masowe"
      data-testid="bulk-actions-toolbar"
    >
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onApprove}
          disabled={isDisabled}
          aria-label={`Zatwierdź ${selectedCount} zaznaczonych rekomendacji`}
          title={isOverLimit ? `Maksymalnie ${MAX_BULK_ITEMS} rekomendacji na raz` : undefined}
          data-testid="bulk-approve-button"
          className={`
            inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium
            transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2
            ${
              isDisabled
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-green-600 text-white hover:bg-green-700 focus:ring-green-500"
            }
          `}
        >
          {isLoading ? (
            <span
              className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
              aria-hidden="true"
            />
          ) : (
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          )}
          Zatwierdź ({selectedCount})
        </button>

        <button
          type="button"
          onClick={onReject}
          disabled={isDisabled}
          aria-label={`Odrzuć ${selectedCount} zaznaczonych rekomendacji`}
          title={isOverLimit ? `Maksymalnie ${MAX_BULK_ITEMS} rekomendacji na raz` : undefined}
          data-testid="bulk-reject-button"
          className={`
            inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium
            transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2
            ${
              isDisabled
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500"
            }
          `}
        >
          {isLoading ? (
            <span
              className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
              aria-hidden="true"
            />
          ) : (
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          )}
          Odrzuć ({selectedCount})
        </button>
      </div>

      <div className="text-sm text-gray-600">
        Zaznaczono: {selectedCount} z {totalCount}
        {isOverLimit && (
          <span className="ml-2 text-red-600 font-medium" data-testid="bulk-limit-warning">
            Maksymalnie {MAX_BULK_ITEMS} rekomendacji na raz
          </span>
        )}
      </div>
    </div>
  );
}

export default BulkActionsToolbar;
